import { Trans, useLingui } from "@lingui/react/macro";
import { CheckIcon, XIcon } from "lucide-react";
import { useState } from "react";

import { cn } from "@/shared/lib";

import { FAILURE_LABELS } from "../../model/FAILURE_LABELS";
import { FRESHNESS_LABELS } from "../../model/FRESHNESS_LABELS";
import type { DashboardDay, DashboardFailure, DashboardFreshness } from "../../model/TeamDashboard";
import { BarList } from "./BarList";
import { DashboardCard } from "./DashboardCard";
import { dayDate } from "./dayDate";

type AnalysesChartProps = {
  days: DashboardDay[];
  failures: DashboardFailure[];
  medianSeconds: number | null | undefined;
  freshness: DashboardFreshness[];
};

// «Разборы по дням»: готовые (зелёные) и упавшие (красные) столбцами, наведение — день в строке
// над графиком; ниже — сколько обычно идёт разбор, почему разборы падали и через сколько после
// конца матча их запускают (доли).
export const AnalysesChart = ({ days, failures, medianSeconds, freshness }: AnalysesChartProps) => {
  const { i18n, t } = useLingui();
  const freshPercent = new Intl.NumberFormat(i18n.locale, {
    style: "percent",
    maximumFractionDigits: 0,
  });
  const freshTotal = freshness.reduce((sum, row) => sum + row.analyses, 0);
  const [hover, setHover] = useState<number | null>(null);
  const long = new Intl.DateTimeFormat(i18n.locale, {
    day: "numeric",
    month: "long",
    weekday: "short",
    // eslint-disable-next-line lingui/no-unlocalized-strings -- часовой пояс, а не текст
    timeZone: "UTC",
  });
  const peak = Math.max(1, ...days.map((day) => day.analyses_done + day.analyses_failed));
  const done = days.reduce((sum, day) => sum + day.analyses_done, 0);
  const failed = days.reduce((sum, day) => sum + day.analyses_failed, 0);
  const hovered = hover === null ? undefined : days[hover];
  const hoveredDay = hovered === undefined ? "" : long.format(dayDate(hovered.day));
  const hoveredDone = hovered?.analyses_done ?? 0;
  const hoveredFailed = hovered?.analyses_failed ?? 0;

  return (
    <DashboardCard
      title={<Trans>Analyses by day</Trans>}
      aside={
        <div className="flex items-center gap-3.5 text-[13px] text-muted">
          <span className="inline-flex items-center gap-1.5">
            <CheckIcon aria-hidden="true" className="size-3.5 text-good" />
            <span aria-hidden="true" className="size-2.5 rounded-sm bg-good" />
            <Trans>Done</Trans>
          </span>
          <span className="inline-flex items-center gap-1.5">
            <XIcon aria-hidden="true" className="size-3.5 text-bad" />
            <span aria-hidden="true" className="size-2.5 rounded-sm bg-bad" />
            <Trans>Failed</Trans>
          </span>
        </div>
      }
    >
      <p role="status" className="min-h-5 text-[13px] text-muted">
        {hovered === undefined
          ? t`Done ${done}, failed ${failed}. Hover a column to see the day.`
          : t`${hoveredDay}: done ${hoveredDone}, failed ${hoveredFailed}`}
      </p>
      <div
        className="flex h-[150px] items-end border-b border-border"
        onMouseLeave={() => {
          setHover(null);
        }}
      >
        {days.map((day, index) => {
          const total = day.analyses_done + day.analyses_failed;
          return (
            <span
              key={day.day}
              className="flex h-full flex-1 items-end justify-center"
              onMouseEnter={() => {
                setHover(index);
              }}
            >
              <span
                className={cn(
                  // Столбец — доля ячейки дня, не шире 24 px: 90 дней на телефоне тоже влезают.
                  "flex w-3/5 max-w-6 flex-col",
                  day.analyses_failed > 0 && "gap-0.5",
                  hover === index && "brightness-125",
                )}
                style={{ height: `${String((total / peak) * 100)}%` }}
              >
                {day.analyses_failed > 0 && (
                  <span
                    className="min-h-[3px] rounded-t bg-bad"
                    style={{ flex: `${String(day.analyses_failed)} 0 0` }}
                  />
                )}
                {day.analyses_done > 0 && (
                  <span
                    className={cn("bg-good", day.analyses_failed === 0 && "rounded-t")}
                    style={{ flex: `${String(day.analyses_done)} 0 0` }}
                  />
                )}
              </span>
            </span>
          );
        })}
      </div>
      <div className="grid grid-cols-[repeat(auto-fit,minmax(150px,1fr))] gap-3 pt-1">
        <div className="flex flex-col">
          <span className="text-xs text-subtle">
            <Trans>An analysis usually takes</Trans>
          </span>
          <span className="font-mono text-lg font-semibold">
            {medianSeconds == null ? "—" : t`${medianSeconds} s`}
          </span>
        </div>
        <div className="flex flex-col gap-0.5">
          <span className="text-xs text-subtle">
            <Trans>Why they failed</Trans>
          </span>
          {failures.length === 0 ? (
            <span className="text-[13px] text-muted">
              <Trans>No failures</Trans>
            </span>
          ) : (
            failures.map((failure) => (
              <span key={failure.code} className="flex justify-between gap-2.5 text-[13px]">
                <span className="text-foreground-soft">{i18n._(FAILURE_LABELS[failure.code])}</span>
                <span className="font-mono">{failure.count}</span>
              </span>
            ))
          )}
        </div>
      </div>
      {freshTotal > 0 && (
        <div className="flex flex-col gap-2.5 border-t border-border/60 pt-3">
          <span className="text-xs text-subtle">
            <Trans>How long after the match ended the analysis was started</Trans>
          </span>
          <BarList
            data-list="freshness"
            rows={freshness.map((row) => ({
              key: row.bucket,
              label: i18n._(FRESHNESS_LABELS[row.bucket]),
              value: row.analyses,
              valueText: freshPercent.format(row.analyses / freshTotal),
            }))}
          />
        </div>
      )}
    </DashboardCard>
  );
};
